// @flow

import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';

import { useResourceFetch } from '@gandi/resource/react';

import type { FetchParams } from '@gandi/resource/redux';

import { getDataDependency, getDisplayName } from 'gandi.react-helpers';

import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  templatesResource,
  type TemplateSummary,
} from '../../modules/cloud/resources/templates';
import type { FullState, PreFetchArgs, PreFetchReturn } from '../../types';

import { selectFetchAccountParams } from './account';
import type { FetchAccountParams } from './account';

export type FetchTemplatesParams = FetchAccountParams;

export function useTemplates(): {
  fetchTemplatesParams: FetchTemplatesParams,
  isFailureTemplates: boolean,
  isFetchingTemplates: boolean,
  isForbiddenTemplates: boolean,
  isLoadedTemplates: boolean,
  label: string,
  shouldFetchTemplates: boolean,
  templates: ?Array<TemplateSummary>,
} {
  const fetchTemplatesParams = useAppSelector(selectFetchAccountParams);
  const {
    summaries: templates,
    isFailure: isFailureTemplates,
    isFetching: isFetchingTemplates,
    isForbidden: isForbiddenTemplates,
    isLoaded: isLoadedTemplates,
    label,
    shouldFetch: shouldFetchTemplates,
  } = useResourceFetch(
    templatesResource,
    // $FlowFixMe[incompatible-cast]
    (fetchTemplatesParams: FetchParams),
  );

  return {
    fetchTemplatesParams,
    isFailureTemplates,
    isFetchingTemplates,
    isForbiddenTemplates,
    isLoadedTemplates,
    label,
    shouldFetchTemplates,
    templates,
  };
}

export function preFetchTemplates(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  const fetchParams = selectFetchAccountParams(args.getState());
  const label = templatesResource.computeLabel(
    templatesResource.options.fetchPath,
    // $FlowFixMe[incompatible-cast]
    (fetchParams: FetchParams),
  );
  return args.dispatch(
    templatesResource.fetch(templatesResource.options.fetchPath, {
      label,
      // $FlowFixMe[incompatible-call]
      params: fetchParams,
    }),
  );
}

export function withTemplates<C: React.ComponentType<any>>(
  WrappedComponent: C,
): any {
  function WithTemplates(props: React.ElementConfig<C>) {
    const templatesProps = useTemplates();

    return <WrappedComponent {...props} {...templatesProps} />;
  }

  const HoistedComponent = hoistNonReactStatics(
    WithTemplates,
    WrappedComponent,
  );

  HoistedComponent.WrappedComponent = WrappedComponent;
  HoistedComponent.displayName = `WithTemplates(${getDisplayName(
    WrappedComponent,
  )})`;
  HoistedComponent.preFetch = getDataDependency(WrappedComponent, 'preFetch');

  return HoistedComponent;
}
